import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

interface ExchangeRatesResponse {
  base: string;
  rates: Record<string, number>;
}

async function fetchExchangeRates(): Promise<ExchangeRatesResponse> {
  const res = await fetch("/api/exchange-rates");
  if (!res.ok) {
    throw new Error("Failed to fetch exchange rates");
  }
  return res.json() as Promise<ExchangeRatesResponse>;
}

export function useExchangeRates() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["exchange-rates"],
    queryFn: fetchExchangeRates,
    staleTime: 1000 * 60 * 60 * 12,
  });

  const rates = data?.rates;

  const localToUsd = useCallback(
    (amount: number, currencyCode: string): number => {
      if (currencyCode === "USD" || !rates?.[currencyCode]) {
        return amount;
      }
      return Math.round((amount / rates[currencyCode]) * 100) / 100;
    },
    [rates],
  );

  const usdToLocal = useCallback(
    (amount: number, currencyCode: string): number => {
      if (currencyCode === "USD" || !rates?.[currencyCode]) {
        return amount;
      }
      return Math.round(amount * rates[currencyCode] * 100) / 100;
    },
    [rates],
  );

  return { rates, isLoading, isError, localToUsd, usdToLocal };
}
